import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, FormControl } from "@angular/forms";
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { Observable, Subject, Subscription, from, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from "rxjs/operators";
import { CriticalPointTemplate } from "src/app/classes/model/practice-path";
import {
  CriticalPointTemplateSearchParams,
  CriticalPointTemplateService,
} from "../../services/critical-point-template.service";

const SEARCH_DEBOUNCE_MS = 300;
const MAX_RESULTS = 60;

@Component({
  selector: "app-critical-point-template-autocomplete",
  templateUrl: "./critical-point-template-autocomplete.component.html",
  styleUrls: ["./critical-point-template-autocomplete.component.scss"],
})
export class CriticalPointTemplateAutocompleteComponent implements OnInit, OnDestroy, OnChanges {
  @Input() initialValue = "Kritikus pont sablon";
  @Input() titleFormControl: AbstractControl<string | null> | null = null;
  @Input() inputTabIndex = 0;
  @Input() isFocusInitial = false;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  @Output() fieldChange = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<CriticalPointTemplate | null>();

  private readonly defaultSearchControl = new FormControl<string>("");
  protected searchControl: FormControl<string> = this.defaultSearchControl;
  protected readonly maxResults = MAX_RESULTS;
  protected options: CriticalPointTemplate[] = [];
  protected isLoading = false;
  protected lastSearchTerm = "";

  private readonly destroy$ = new Subject<void>();
  private controlSubscription: Subscription | null = null;
  private componentInitialized = false;
  private lastSelectionDate: Date | null = null;

  constructor(private readonly templateService: CriticalPointTemplateService) {}

  ngOnInit(): void {
    const controlToBind = this.resolveControl();
    this.bindToControl(controlToBind);

    if (this.initialValue !== null && this.initialValue !== undefined && this.shouldApplyInitialValue(controlToBind)) {
      this.setControlValue(this.initialValue);
    }

    this.componentInitialized = true;
  }

  isPanelOpen(): boolean {
    return this.autocompleteTrigger?.panelOpen ?? false;
  }

  ngOnDestroy(): void {
    this.controlSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["titleFormControl"] && this.componentInitialized) {
      const controlToBind = this.resolveControl();
      this.bindToControl(controlToBind);
    }

    if (
      changes["initialValue"] &&
      this.componentInitialized &&
      changes["initialValue"].currentValue !== undefined &&
      this.shouldApplyInitialValue(this.searchControl)
    ) {
      this.setControlValue(changes["initialValue"].currentValue);
    }
  }

  protected displayTemplate(template: CriticalPointTemplate | null): string {
    return typeof template === "string" ? template : template?.title ?? "";
  }

  protected onOptionSelected(
    event: MatAutocompleteSelectedEvent
  ): void {
    const selected = event.option.value as CriticalPointTemplate | null;

    const normalizedTitle = selected?.title ?? "";
    this.searchControl.setValue(normalizedTitle, { emitEvent: false });
    this.searchControl.markAsDirty();
    this.searchControl.markAsTouched();
    this.searchControl.updateValueAndValidity({ emitEvent: false });
    this.lastSearchTerm = normalizedTitle.trim();
    this.fieldChange.emit(this.lastSearchTerm);
    this.selectionChange.emit(selected);
    this.templateService.incrementUsageCount(selected?.id ?? "");
    this.lastSelectionDate = new Date();
  }


  private queryTemplates(term: string): Observable<CriticalPointTemplate[]> {
    const normalized = term.trim();
    if (!normalized) {
      this.isLoading = false;
      return of([]);
    }

    this.isLoading = true;
    const params: CriticalPointTemplateSearchParams = {
      page: 1,
      pageSize: this.maxResults,
      filterLogic: "OR",
      titleContains: normalized,
      descriptionContains: normalized,
      sortBy:"usageCount",
      sortOrder:"desc",
    };

    return from(this.templateService.searchTemplates(params)).pipe(
      map((response) => response?.items ?? []),
      catchError(() => of([])),
      finalize(() => {
        this.isLoading = false;
      })
    );
  }


  private setControlValue(value: string | null): void {
    const normalized = value ?? "";
    this.searchControl.setValue(normalized, { emitEvent: false });
    this.lastSearchTerm = normalized.trim();
  }

  private resolveControl(): FormControl<string> {
    if (this.titleFormControl instanceof FormControl) {
      return this.titleFormControl as FormControl<string>;
    }

    return this.defaultSearchControl;
  }

  private bindToControl(control: FormControl<string>): void {
    if (this.searchControl === control && this.controlSubscription) {
      return;
    }

    this.controlSubscription?.unsubscribe();
    this.searchControl = control;

    this.controlSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        map((value) => {
          if (value == null) {
            return "";
          }
          if (typeof value === "string") {
            return value.trim();
          }
          return (value as CriticalPointTemplate)?.title?.trim() ?? "";
        }),
        distinctUntilChanged(),
        tap((rawValue) => {
          this.lastSearchTerm = rawValue;
          this.fieldChange.emit(rawValue);
        }),
        switchMap((newValue) => this.queryTemplates(newValue)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.options = results.slice(0, this.maxResults);
        if (this.options.length === 0) {
          if (this.isPanelOpen()) {
            this.autocompleteTrigger.closePanel();
          }
        } else if (!this.isPanelOpen()) {
          // Ha az utolsó selection <1 másodperce volt, akkor nem jelenítünk meg semmit
          // Hiszen ilyenkor ez történne: Kiválaszt egyet -> átíródik a field -> ennek hatására triggerelődött ez a valueChanges
          // viszont ilyenkor nem akarjuk megjeleníteni megint a panelt, hiszen már választott
          if (this.lastSelectionDate && new Date().getTime() - this.lastSelectionDate.getTime() < 1000) {
            return;
          }
          if (!this.isSearchInputFocused()) {
            return;
          }
          this.autocompleteTrigger?.openPanel();
        }
      });
  }

  private shouldApplyInitialValue(control: FormControl<string>): boolean {
    const currentValue = control.value;
    if (currentValue == null) {
      return true;
    }

    if (typeof currentValue === "string") {
      return currentValue.trim().length === 0;
    }

    return false;
  }

  private isSearchInputFocused(): boolean {
    return this.searchInput?.nativeElement === document.activeElement;
  }
}

